import React, { useEffect, useState } from "react";

import axios from "axios";

import {
  CalendarDays,
  Save,
  Loader2,
  CheckCircle2,
} from "lucide-react";


// =========================
// API Base
// =========================
const API_BASE =
  process.env.REACT_APP_API_URL ||
  (window.location.hostname === "localhost"
    ? "http://localhost:5000"
    : "https://upsc-backend-353e.onrender.com");



const ExamCycleManager = () => {

  const user = JSON.parse(
    localStorage.getItem("user")
  );

  const token = user?.token;


  // =========================
  // Form State
  // =========================
  const [formData, setFormData] =
    useState({

      examType: "prelims",

      year: new Date().getFullYear(),

      examDate: "",

      expectedMonth: "",

      isTentative: false,

      isActive: true,
    });


  // =========================
  // UI State
  // =========================
  const [loading, setLoading] =
    useState(false);

  const [success, setSuccess] =
    useState("");

  const [cycles, setCycles] =
    useState([]);

    const [cycleFilter, setCycleFilter] =
  useState("all");

    const [editingId, setEditingId] =
  useState(null);



  // =========================
  // Fetch Existing Cycles
  // =========================
  const fetchCycles = async () => {

    try {

      const res = await axios.get(
  `${API_BASE}/api/exam-cycles/admin/all`,
  {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }
);

      setCycles(
        res.data.cycles || []
      );

    } catch (err) {

      console.error(err);
    }
  };


  useEffect(() => {

    fetchCycles();

  }, []);




  // =========================
  // Handle Change
  // =========================
  const handleChange = (e) => {

    const {
      name,
      value,
      type,
      checked,
    } = e.target;

    setFormData((prev) => ({
      ...prev,

      [name]:
        type === "checkbox"
          ? checked
          : value,
    }));
  };




  // =========================
  // Submit
  // =========================
  const handleSubmit = async (e) => {

    e.preventDefault();

    setLoading(true);

    setSuccess("");

    try {

      await axios.post(

        `${API_BASE}/api/exam-cycles/admin/save`,

        formData,

        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );


      setSuccess(
  editingId
    ? "Exam cycle updated successfully"
    : "Exam cycle saved successfully"
);

      fetchCycles();
      setEditingId(null);

    } catch (err) {

      console.error(err);

      alert(
        err?.response?.data?.message ||
        "Failed to save exam cycle"
      );
    }

    setLoading(false);
  };

  // =========================
// Delete Cycle
// =========================
const handleDeleteCycle = async (
  cycleId
) => {

  if (
    !window.confirm(
      "Delete this exam cycle?"
    )
  ) {
    return;
  }

  try {

    await axios.delete(
      `${API_BASE}/api/exam-cycles/admin/${cycleId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    fetchCycles();

  } catch (err) {

    console.error(err);

    alert(
      err?.response?.data?.message ||
      "Failed to delete cycle"
    );
  }
};

// =========================
// Edit Cycle
// =========================
const handleEditCycle = (
  cycle
) => {

  setEditingId(cycle._id);

  setFormData({
    examType: cycle.examType,
    year: cycle.year,

    examDate: cycle.examDate
      ? new Date(cycle.examDate)
          .toISOString()
          .split("T")[0]
      : "",

    expectedMonth:
      cycle.expectedMonth || "",

    isTentative:
      cycle.isTentative,

    isActive:
      cycle.isActive,
  });

  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });
};

const filteredCycles =
  cycleFilter === "all"
    ? cycles
    : cycles.filter(
        (c) =>
          c.isActive ===
          (cycleFilter === "active")
      );


  return (

    <div className="min-h-screen text-white p-6 md:p-10">

      {/* Header */}
      <div className="mb-10">

        <h1 className="text-4xl font-extrabold mb-3 bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">

          UPSC Exam Cycle Manager
        </h1>

        <p className="text-gray-400 text-lg">

          Manage Prelims & Mains countdown cycles dynamically.
        </p>
      </div>



      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">

        {/* ========================= */}
        {/* Form Section */}
        {/* ========================= */}

        <div className="xl:col-span-2">

          <div className="bg-zinc-900 border border-zinc-800 rounded-3xl shadow-2xl overflow-hidden">

            {/* Top Glow */}
            <div className="h-2 bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-500" />

            <div className="p-8">

              <div className="flex items-center gap-3 mb-8">

                <CalendarDays className="w-8 h-8 text-cyan-400" />

                <h2 className="text-2xl font-bold">
  {editingId
    ? "Edit Exam Cycle"
    : "Create Exam Cycle"}
</h2>
              </div>



              <form
                onSubmit={handleSubmit}
                className="space-y-6"
              >

                {/* Exam Type */}
                <div>

                  <label className="block mb-2 text-sm font-semibold text-gray-300">

                    Exam Type
                  </label>

                  <select
                    name="examType"
                    value={formData.examType}
                    onChange={handleChange}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-2xl px-5 py-4 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  >
                    <option value="prelims">
                      Prelims
                    </option>

                    <option value="mains">
                      Mains
                    </option>
                  </select>
                </div>



                {/* Year */}
                <div>

                  <label className="block mb-2 text-sm font-semibold text-gray-300">

                    Year
                  </label>

                  <input
                    type="number"
                    name="year"
                    value={formData.year}
                    onChange={handleChange}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-2xl px-5 py-4 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                </div>



                {/* Exam Date */}
                <div>

                  <label className="block mb-2 text-sm font-semibold text-gray-300">

                    Official Exam Date
                  </label>

                  <input
                    type="date"
                    name="examDate"
                    value={formData.examDate}
                    onChange={handleChange}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-2xl px-5 py-4 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                </div>



                {/* Expected Month */}
                <div>

                  <label className="block mb-2 text-sm font-semibold text-gray-300">

                    Expected Month
                  </label>

                  <input
                    type="text"
                    name="expectedMonth"
                    value={formData.expectedMonth}
                    onChange={handleChange}
                    placeholder="Example: May 2027"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-2xl px-5 py-4 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                </div>



                {/* Toggles */}
                <div className="grid md:grid-cols-2 gap-5">

                  <label className="flex items-center gap-3 bg-zinc-800 border border-zinc-700 rounded-2xl px-5 py-4 cursor-pointer">

                    <input
                      type="checkbox"
                      name="isTentative"
                      checked={formData.isTentative}
                      onChange={handleChange}
                      className="w-5 h-5 accent-cyan-500"
                    />

                    <span className="font-medium">
                      Tentative Cycle
                    </span>
                  </label>



                  <label className="flex items-center gap-3 bg-zinc-800 border border-zinc-700 rounded-2xl px-5 py-4 cursor-pointer">

                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleChange}
                      className="w-5 h-5 accent-cyan-500"
                    />

                    <span className="font-medium">
                      Set Active
                    </span>
                  </label>
                </div>



                {/* Success */}
                {success && (

                  <div className="flex items-center gap-2 bg-green-500/20 border border-green-500/40 text-green-300 px-5 py-4 rounded-2xl">

                    <CheckCircle2 className="w-5 h-5" />

                    {success}
                  </div>
                )}



                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-3 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold py-4 rounded-2xl transition-all duration-300 shadow-xl hover:shadow-cyan-500/20"
                >

                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-5 h-5" />

{editingId
  ? "Update Exam Cycle"
  : "Save Exam Cycle"}
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>



        {/* ========================= */}
        {/* Active Cycles */}
        {/* ========================= */}

        <div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-3xl shadow-2xl overflow-hidden">

            <div className="h-2 bg-gradient-to-r from-purple-500 to-pink-500" />

            <div className="p-6">

              <div className="flex items-center justify-between mb-6">

  <h2 className="text-2xl font-bold">
    Exam Cycles
  </h2>

  <select
    value={cycleFilter}
    onChange={(e) =>
      setCycleFilter(e.target.value)
    }
    className="bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-sm"
  >
    <option value="all">
      All
    </option>

    <option value="active">
      Active
    </option>

    <option value="inactive">
      Inactive
    </option>
  </select>

</div>



              <div className="space-y-5">

                {cycles.length === 0 ? (

                  <div className="text-gray-400">

                    No active cycles found.
                  </div>

                ) : (

                  filteredCycles.map((cycle) => (

                    <div
                      key={cycle._id}
                      className="bg-zinc-800 border border-zinc-700 rounded-2xl p-5"
                    >

                      <div className="flex items-center justify-between mb-3">

                        <h3 className="text-lg font-bold capitalize">

                          {cycle.examType}
                        </h3>

                        <div className="flex gap-2">

  <span className="px-3 py-1 rounded-full bg-cyan-500/20 text-cyan-300 text-xs font-semibold">
    {cycle.year}
  </span>

  <span
    className={`px-3 py-1 rounded-full text-xs font-semibold ${
      cycle.isActive
        ? "bg-green-500/20 text-green-300"
        : "bg-gray-500/20 text-gray-300"
    }`}
  >
    {cycle.isActive
      ? "Active"
      : "Inactive"}
  </span>

</div>
                      </div>



                      {cycle.isTentative ? (

                        <p className="text-yellow-400 text-sm">

                          Tentative • Expected {cycle.expectedMonth}
                        </p>

                      ) : (

                        <p className="text-green-400 text-sm">

                          Official •{" "}

                          {new Date(
                            cycle.examDate
                          ).toDateString()}
                        </p>
                      )}
                     <div className="mt-4 flex justify-end gap-2">

  <button
    onClick={() =>
      handleEditCycle(cycle)
    }
    className="px-4 py-2 rounded-xl bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium transition"
  >
    Edit
  </button>

  <button
    onClick={() =>
      handleDeleteCycle(cycle._id)
    }
    className="px-4 py-2 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-medium transition"
  >
    Delete
  </button>

</div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExamCycleManager;